import { useContext, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import useCheckoutPageProps from "@hooks/useCheckoutPageProps.ts";

import { validatePhoneApi } from "@/api/checkoutApi";
import useAppStateSync from "@/hooks/useAppStateSync";
import { CheckoutContext } from "@/providers/context/CheckoutContext";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAtom } from "jotai";
import * as Yup from "yup";

import { Box, Checkbox, Flex, Link, Show } from "@chakra-ui/react";

import { CheckoutFlowEnum } from "@ts/enums/CheckoutFlowEnum";
import { ClassesEnum } from "@ts/enums/ClassesEnum";

import {
    checkoutAtom,
    createSubscriptionAtom,
    isNewsletterSubEnabledAtom,
    isPaymentPendingAtom
} from "@atoms/checkoutAtom";

import {
    addressRegex,
    formatPrice,
    getTrackingId,
    phoneRegex,
    postalCodeRegex,
    suburbOrCityRegex
} from "@utils/utils";

import { Alert } from "@components/Alert";
import { Button } from "@components/Button";
import { ReCaptcha } from "@components/ReCaptcha";
import { Text } from "@components/Text";

import { BillingAddressContainer } from "./containers/BillingAddressContainer";
import { BillingAddressInfoInterface } from "./containers/BillingAddressContainer/BillingAddressContainer";
import { CardPaymentContainer } from "./containers/CardPaymentContainer";
import { CardPaymentContainerRef } from "./containers/CardPaymentContainer/CardPaymentContainer";

declare function route(name: string, params?: object): string;

export interface PriceIdApiInterface {
    item_price_id: string;
    item_type: "plan" | "addon";
}

interface PropTypes {
    isHundredPercentOff: boolean;
    priceIds: PriceIdApiInterface[];
    onTrackWithHeap: (event: string, data?: object) => void;
    onDispatchGAEvents: () => void;
}

const PaymentCaptureContainer = ({
    isHundredPercentOff = false,
    priceIds,
    onTrackWithHeap,
    onDispatchGAEvents
}: PropTypes) => {
    const [searchParams] = useSearchParams();
    const { t, i18n } = useTranslation();
    const searchParamCountry = searchParams.get("selectedCountry");
    const searchParamState = searchParams.get("selectedState");

    const cardPaymentContainerRef = useRef<null | CardPaymentContainerRef>(
        null
    );

    const { userSelectedCountry } = useContext(CheckoutContext);

    const { allCountries = [] } = useCheckoutPageProps();

    const auBillingFormValidationSchema = useMemo(
        () =>
            Yup.object().shape({
                countryInput: Yup.string().required(
                    t("Please select a country")
                ),
                addressLine1Input: Yup.string()
                    .required(t("Address line 1 is required"))
                    .matches(
                        addressRegex,
                        t("Please enter a valid address line 1")
                    ),
                addressLine2Input: Yup.string()
                    .notRequired()
                    .test(
                        "is valid address line 2",
                        t("Please enter a valid address line 2"),
                        value => !value || addressRegex.test(value)
                    ),
                cityInput: Yup.string()
                    .required(t("Suburb or city is required"))
                    .matches(
                        suburbOrCityRegex,
                        t("Please enter a valid suburb or city")
                    ),
                inputState: Yup.string()
                    .default("")
                    .when("countryInput", {
                        is: (value: string) => {
                            const country = allCountries.find(
                                c => c.value === value
                            )!;
                            return country.states.length > 0;
                        },
                        then: schema =>
                            schema.required(t("Please enter a valid state")),
                        otherwise: schema => schema.notRequired()
                    }),
                postalCodeInput: Yup.string()
                    .required(t("Postal code is required"))
                    .matches(
                        postalCodeRegex,
                        t("Please enter a valid postal code")
                    ),
                countryCodeInput: Yup.string().required(
                    t("Please select a country code")
                ),
                phoneNumberInput: Yup.string()
                    .required(t("Phone number is required"))
                    .matches(phoneRegex, t("Please enter a valid phone number"))
            }),
        [allCountries, i18n.language]
    );

    const billingFormValidationSchema = useMemo(
        () =>
            Yup.object().shape({
                countryInput: Yup.string().required(
                    t("Please select a country")
                ),
                addressLine1Input: Yup.string().required(
                    t("Address line 1 is required")
                ),
                addressLine2Input: Yup.string().notRequired(),
                cityInput: Yup.string().required(
                    t("Suburb or city is required")
                ),
                inputState: Yup.string()
                    .default("")
                    .when("countryInput", {
                        is: (value: string) => {
                            const country = allCountries.find(
                                c => c.value === value
                            )!;
                            return country?.states?.length > 0;
                        },
                        then: schema =>
                            schema.required(t("Please enter a valid state")),
                        otherwise: schema => schema.notRequired()
                    }),
                postalCodeInput: Yup.string().notRequired(),
                countryCodeInput: Yup.string().required(
                    t("Please select a country code")
                ),
                phoneNumberInput: Yup.string()
                    .required(t("Phone number is required"))
                    .matches(phoneRegex, t("Please enter a valid phone number"))
            }),
        [allCountries, i18n.language]
    );

    const formMethods = useForm<BillingAddressInfoInterface>({
        resolver: yupResolver(
            userSelectedCountry === "AU"
                ? auBillingFormValidationSchema
                : billingFormValidationSchema
        ),
        reValidateMode: "onChange",
        mode: "onChange",
        defaultValues: {
            countryInput: searchParamCountry ?? "",
            inputState: searchParamState ?? "",
            countryCodeInput: ""
        }
    });

    const {
        formState: { errors, touchedFields, isValid: isBillingInfoFormValid },
        handleSubmit,
        setError,
        clearErrors,
        resetField,
        control
    } = formMethods;

    const [isTermsSelected, setIsTermsSelected] = useState<boolean>(false);
    const [isPaymentEnabled, setIsPaymentEnabled] = useState(false);
    const [paymentError, setPaymentError] = useState<{
        hasError: boolean;
        message: string;
    }>({ hasError: false, message: "" });

    const [billingDetails, setBillingDetails] = useState({});
    const [isCaptchaVerified, setIsCaptchaVerified] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);

    const [checkout] = useAtom(checkoutAtom);
    const [createSubscriptionState] = useAtom(createSubscriptionAtom);
    const [isPaymentPending] = useAtom(isPaymentPendingAtom);
    const [isNewsletterSubEnabled, setIsNewsletterSubEnabled] = useAtom(
        isNewsletterSubEnabledAtom
    );

    // Hacky duplicated code for new checkout users with no card details.
    const {
        state: { checkout: checkoutAppState }
    } = useAppStateSync();

    const { existingCards = [], phoneAbstractApiKey } = useCheckoutPageProps();

    console.log("isBillingInfoFormValid", isBillingInfoFormValid);

    const checkoutFlow = checkoutAppState?.checkoutFlow ?? null;

    const getCanShowBillingInfo = () => {
        // Renewal flow: Show card + billing
        if (checkoutFlow === CheckoutFlowEnum.PROGRAM_RENEWAL) {
            return true;
        }

        // Add new student for existing parent flow: Show card + billing
        // New parent flow: Show card + billing
        if (
            !(
                checkoutFlow === CheckoutFlowEnum.PREMIUM_SERVICE_PAYMENT ||
                checkoutFlow === CheckoutFlowEnum.SUBJECT_CHANGE_FEE_PAYMENT
            )
        ) {
            return true;
        }

        const studentCards = existingCards.filter(
            card => card.student_id === checkoutAppState?.checkoutStudentId
        );

        // Addons + subject selection:
        // No card found for student: Show card + billing
        if (studentCards.length === 0) {
            return true;
        }

        // Card has no billing details: Show card + billing
        // Since we're filtering by is_primary in BE, we can use the first element in the array
        if (
            studentCards[0].billing_country === null ||
            studentCards[0].billing_state === null ||
            studentCards[0].billing_city === null ||
            studentCards[0].billing_addr1 === null
        ) {
            return true;
        }

        // Card exists and has billing details: Hide billing info
        return false;
    };

    const [showBillingInfo, setShowBillingInfo] = useState<boolean>(
        getCanShowBillingInfo()
    );

    const onInitiatePayment = async () => {
        await cardPaymentContainerRef.current!.initiateCardPayment();
    };

    const onAddressInfoSubmit = async ({
        countryInput,
        addressLine1Input,
        addressLine2Input,
        cityInput,
        inputState,
        postalCodeInput,
        countryCodeInput,
        phoneNumberInput
    }: BillingAddressInfoInterface) => {
        try {
            setIsLoading(true);
            await setBillingDetails({
                country: countryInput,
                line1: addressLine1Input,
                line2: addressLine2Input,
                line3: postalCodeInput,
                city: cityInput,
                state: inputState,
                phone: `${countryCodeInput}${phoneNumberInput}`
            });

            const getPhoneValidation = async () => {
                const phoneValidation = (
                    await validatePhoneApi(
                        `${countryCodeInput}${phoneNumberInput}`,
                        phoneAbstractApiKey
                    )
                ).data as {
                    valid: boolean;
                    country: {
                        code: string;
                        name: string;
                        prefix: string;
                    };
                };

                if (!phoneValidation.valid) {
                    if (window.heap?.track) {
                        window.heap.track("Payment Error", {
                            error: "Invalid phone number"
                        });
                    }
                    setError("phoneNumberInput", {
                        message: "Please enter a valid phone number"
                    });
                    setIsLoading(false);
                    return false;
                }
                clearErrors("phoneNumberInput");
                setIsLoading(false);
                return true;
            };

            if (countryCodeInput && phoneNumberInput) {
                const isValidPhone = await getPhoneValidation();
                if (isValidPhone) {
                    // Process form data in the parent component
                    onInitiatePayment();
                }
            } else {
                onInitiatePayment();
            }
        } catch (error) {
            console.error(error);
            setIsLoading(false);
            return;
        } finally {
            setIsLoading(false);
        }
    };

    const onCaptchaVerified = (r: boolean) => setIsCaptchaVerified(r);

    const onToggleTermsAcceptance = (v: boolean) => setIsTermsSelected(v);

    const onTogglePaymentEnable = (isEnabled: boolean) =>
        setIsPaymentEnabled(isEnabled);

    const onSetPaymentError = (error: {
        hasError: boolean;
        message: string;
    }) => {
        if (window.heap?.track) {
            window.heap.track("Payment Error", {
                error: error.message
            });
        }
        setPaymentError(error);
    };

    return (
        <FormProvider {...formMethods}>
            <Flex
                bg="white"
                direction="column"
                pt={[8, null, null, 28]}
                gap={8}
                align="start"
                px={[5, null, null, 18, 20]}
                w={["full", null, null, null, null, 700]}
                mr={[0, null, "auto"]}
                as="form"
                autoComplete="off"
            >
                <ReCaptcha
                    isCaptchaVerified={isCaptchaVerified}
                    onCaptchaVerified={onCaptchaVerified}
                />
                <CardPaymentContainer
                    ref={cardPaymentContainerRef}
                    isHundredPercentOff={isHundredPercentOff}
                    priceIds={priceIds}
                    onDispatchGAEvents={onDispatchGAEvents}
                    billingDetails={billingDetails}
                    shouldCaptureBillingInfo={showBillingInfo}
                    trackWithHeap={onTrackWithHeap}
                    onTogglePaymentEnable={onTogglePaymentEnable}
                    onSetPaymentError={onSetPaymentError}
                    onAddNewPaymentMethod={val => setShowBillingInfo(val)}
                />

                {showBillingInfo && (
                    <BillingAddressContainer
                        errors={errors}
                        control={control}
                        touchedFields={touchedFields}
                        onResetField={resetField}
                    />
                )}

                <Text fontSize={14}>
                    {t(
                        "checkoutDetails.paymentCapture.privacy",
                        "Your personal data will be used to process your order, support your experience throughout this website, and for other purposes described in our "
                    )}
                    {/* // TODO: Improve hardcorded check for contries don't need state selections */}
                    <Link
                        fontSize={14}
                        href={
                            userSelectedCountry === "VN"
                                ? route("external.ecom-info.privacy-policy-vn")
                                : route("external.ecom.privacy-policy")
                        } // nosonar
                        color="#06F"
                        target="_blank"
                    >
                        {" "}
                        {t(
                            "checkoutDetails.paymentCapture.privacyPolicy",
                            "privacy policy."
                        )}
                    </Link>
                </Text>
                <Checkbox
                    className={ClassesEnum.Checkbox}
                    data-tracking-id={getTrackingId(
                        [
                            ClassesEnum.CardDetailsContainer,
                            ClassesEnum.Checkbox
                        ],
                        "checkTermsAndConditions"
                    )}
                    size="md"
                    onChange={e => onToggleTermsAcceptance(e.target.checked)}
                    data-testid="termsCheckbox"
                >
                    {t(
                        "checkout.terms",
                        "I have read and agree to the website"
                    )}
                    {/* // TODO: Improve hardcorded check for contries don't need state selections */}
                    <Link
                        fontSize={14}
                        href={
                            userSelectedCountry === "VN"
                                ? route(
                                      "external.ecom-info.terms-conditions-vn"
                                  )
                                : route("external.ecom.terms-conditions")
                        } // nosonar
                        color="#06F"
                        target="_blank"
                    >
                        {" "}
                        {t(
                            "checkout.termsAndCondition",
                            "terms and conditions *"
                        )}
                    </Link>
                </Checkbox>
                <Checkbox
                    className={ClassesEnum.Checkbox}
                    data-tracking-id={getTrackingId(
                        [
                            ClassesEnum.CardDetailsContainer,
                            ClassesEnum.Checkbox
                        ],
                        "checkNewsletterSubscription"
                    )}
                    defaultChecked={checkout.user.receiveOfferNews === true}
                    onChange={() =>
                        setIsNewsletterSubEnabled(!isNewsletterSubEnabled)
                    }
                    size="md"
                >
                    {t(
                        "checkout.receiveOfferNews",
                        "Receive free access to useful resources, tips and offers about online education"
                    )}
                </Checkbox>
                <Button
                    className={ClassesEnum.Button}
                    data-tracking-id={getTrackingId(
                        [ClassesEnum.CardDetailsContainer, ClassesEnum.Button],
                        "pay"
                    )}
                    isLoading={isPaymentPending || isLoading}
                    isDisabled={
                        !isTermsSelected ||
                        !isCaptchaVerified ||
                        createSubscriptionState.isSuccess ||
                        // If the payment method is a card payment, we check to see the status of the selected payment method
                        !isPaymentEnabled ||
                        isLoading ||
                        (!isBillingInfoFormValid && showBillingInfo)
                    }
                    loadingText="Processing. . ."
                    width="full"
                    onClick={
                        showBillingInfo
                            ? handleSubmit(onAddressInfoSubmit)
                            : onInitiatePayment
                    }
                    colorScheme="ebonyClay"
                >
                    {isHundredPercentOff ? `Pay ${formatPrice(0, "")}` : "Pay"}
                </Button>
                {paymentError.hasError && (
                    <Alert
                        mt={-4}
                        type="error"
                        message={paymentError.message}
                    />
                )}

                <Show below="md">
                    <Box w="100%" minH="60px" />
                </Show>
            </Flex>
        </FormProvider>
    );
};

export default PaymentCaptureContainer;
